import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import AddressLink from '@repo/ui/address-link'
import Badge from '@repo/ui/badge'
import DataTable from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getTripBookingDispatchPlaces } from '../../../api/trip-booking-dispatch-places/get-trip-booking-dispatch-places'
import { useNativeCountryName } from '../../../lib/hooks/use-native-country-name'
import useTranslate from '../../../lib/hooks/use-translate'
import { TransportTypeEnum, TripTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import {
  TripStepTypeEnum,
  tripStepTypeMapping
} from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { CoachCompanyLight } from '../../../lib/types/models/ol/coach-company/coach-company'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripBookingProperties } from '../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { TripBookingDispatchPlaceIndividualTypeEnum } from '../../../lib/types/models/trip-booking/trip-date'
import { TripStepTypes } from '../../../lib/types/models/trip-booking/trip-step'
import { TripBookingDispatchPlace } from '../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place'
import CountryFlag from '../../elements/country-flag'
import LoadingScreen from '../../elements/loading/loading-screen'
import TransportPicto from '../../elements/transport-picto'
import { enrollmentColumns, staffColumns } from './center-trip-bookings.utils'

function LabelWithChildren({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={'flex flex-col gap-2'}>
      <Typography variant={'label-medium'} color={tailwindColors.grey.lighter}>
        {label}
      </Typography>
      {children}
    </div>
  )
}

type TripStepDetailsProps = {
  tripStep: TripStepTypes.Light
  directionality: TripStepTypeEnum
  tripBooking: TripBookingTypes.Light
}

function TripStepDetails({ tripBooking, tripStep, directionality }: TripStepDetailsProps) {
  const { t } = useTranslate()
  return (
    <div className="flex flex-col gap-3 p-2 bg-white-active rounded border border-grey-light">
      <Typography variant={'label-extrabold'}>
        {t(tripStepTypeMapping[directionality].label)}
      </Typography>

      <div className="grid grid-cols-6 gap-7">
        {/* departure date */}
        <LabelWithChildren label={'Date de départ'}>
          <Badge text={DateFormatter.DDMMYYYY(tripStep.departureDate)} />
        </LabelWithChildren>

        {/* departure location */}
        <LabelWithChildren label={'Lieu de départ'}>
          <div className="flex flex-row gap-2">
            <CountryFlag possibleCountry={tripStep.departurePlace.country} />
            <Typography.Link
              variant={'secondary-body'}
              onClick={() => {
                window.open(tripStep.departurePlace.adresseURI, '_blank')
              }}
            >
              {tripStep.departurePlace.label}
            </Typography.Link>
          </div>
        </LabelWithChildren>

        {/* departure time */}
        <LabelWithChildren label={'Heure de départ'}>
          <Badge text={DateFormatter.HHmm(DateFormatter.createDate(tripStep.departureTime))} />
        </LabelWithChildren>

        {/* transport means */}
        <LabelWithChildren label={'Compagnie de transport'}>
          <Typography variant={'tag'}>
            {tripBooking.travelCompany?.name ?? 'Sans Transport'}
          </Typography>
        </LabelWithChildren>

        {/* transport means picto */}
        <LabelWithChildren label={'Transport'}>
          {tripBooking.relatedTrip.transportMeans?.picto ? (
            <TransportPicto picto={tripBooking.relatedTrip.transportMeans.picto} />
          ) : (
            <Typography variant={'tag'}>
              {TripProperties.handleTransportMeansName(tripBooking.relatedTrip.transportMeans)}
            </Typography>
          )}
        </LabelWithChildren>

        {/* transport mean number */}
        <LabelWithChildren label={'N° de vol/train'}>
          <Typography variant={'tag'}>{tripStep.transportMeanNumber}</Typography>
        </LabelWithChildren>

        {/* arrival date */}
        <LabelWithChildren label={"Date d'arrivée"}>
          <Badge text={DateFormatter.DDMMYYYY(tripStep.arrivalDate)} />
        </LabelWithChildren>

        {/* arrival location */}
        <LabelWithChildren label={"Lieu d'arrivée"}>
          <div className="flex flex-row gap-2">
            {tripStep.arrivalPlace?.country && (
              <CountryFlag possibleCountry={tripStep.arrivalPlace.country} />
            )}
            {tripStep.arrivalPlace?.label && (
              <Typography.Link
                variant={'secondary-body'}
                onClick={() => {
                  if (!tripStep.arrivalPlace) return
                  window.open(tripStep.arrivalPlace.adresseURI, '_blank')
                }}
              >
                {tripStep.arrivalPlace?.label ?? '-'}
              </Typography.Link>
            )}
          </div>
        </LabelWithChildren>

        {/* arrival time */}
        <LabelWithChildren label={"Heure d'arrivée"}>
          <Badge text={DateFormatter.HHmm(DateFormatter.createDate(tripStep.arrivalTime))} />
        </LabelWithChildren>

        {/* time before departure for parents */}
        <LabelWithChildren label={'Heure(s) de RDV Parents'}>
          {tripStep.timeBeforeDepartureForParents ? (
            <Badge
              text={DateFormatter.getFormattedTime(tripStep.timeBeforeDepartureForParents)}
              variant={'warning'}
            />
          ) : (
            '-'
          )}
        </LabelWithChildren>
      </div>
    </div>
  )
}

export function TripBookingDetails({
  tripBooking,
  enabled = false,
  centerId,
  tripId
}: {
  tripBooking: TripBookingTypes.Light
  centerId: number
  tripId: number
  enabled: boolean
}) {
  const { tripSteps } = tripBooking
  const nativeCountryName = useNativeCountryName()

  const enrollmentsQuery = useQuery({
    queryKey: ['dispatchPlaces', 'enrollments', tripBooking.id, centerId, tripId],
    queryFn: () =>
      getTripBookingDispatchPlaces<'FULL'>({
        version: 'FULL',
        filter: {
          centerId,
          centerTripId: tripId,
          tripBookingId: tripBooking.id,
          centerTripBookingScope: true,
          individualTypes: [TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT]
        }
      }),
    enabled
  })

  const staffsQuery = useQuery({
    queryKey: ['dispatchPlaces', 'staffs', tripBooking.id, centerId, tripId],
    queryFn: () =>
      getTripBookingDispatchPlaces<'FULL'>({
        version: 'FULL',
        filter: {
          centerId,
          centerTripId: tripId,
          tripBookingId: tripBooking.id,
          centerTripBookingScope: true,
          individualTypes: [
            TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF,
            TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_EXTERNAL_STAFF,
            TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_LEC_STAFF,
            TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_STAFF
          ]
        }
      }),
    enabled
  })

  const studentsData = enrollmentsQuery.data?.data
    ? Array.from(
        new Map(
          enrollmentsQuery.data.data
            .filter((item) => item.enrollment?.id) // Remove items without enrollment.id
            .map((item) => [item.enrollment?.id, item])
        ).values()
      )
    : []

  const staffsData = staffsQuery.data?.data
    ? Array.from(
        new Map(
          staffsQuery.data.data
            .filter((item) => item.affectedStaff?.id) // Remove items without staff.id
            .map((item) => [item.affectedStaff?.id, item])
        ).values()
      )
    : []

  const coachCompany: CoachCompanyLight | undefined =
    tripBooking.directionality === TransportTypeEnum.ONE_WAY
      ? tripBooking.departureCoachCompany
      : tripBooking.arrivalCoachCompany
  const foreignProviderAppointmentPlace =
    TripBookingProperties.getTripStep(tripBooking)?.foreignProviderAppointmentPlace
  const isSecondaryTripBooking = tripBooking.relatedTrip.type === TripTypeEnum.SECONDARY

  return (
    <div className={'flex flex-col gap-4'}>
      {tripSteps.map((tripStep) => (
        <TripStepDetails
          key={tripStep.id}
          tripBooking={tripBooking}
          directionality={tripStep.directionality}
          tripStep={tripStep}
        />
      ))}

      {!isSecondaryTripBooking && (
        <div className={'flex flex-col gap-2'}>
          <Typography variant={'tag'} color={tailwindColors.primary.DEFAULT}>
            Car de transfert du prestataire étranger
          </Typography>

          {!tripBooking.isTakeInCharge && (
            <div className={'grid grid-cols-5 gap-2'}>
              {/* coach company */}
              <LabelWithChildren label={'Coach company'}>
                <Typography variant={'tag'}>{valueRender(coachCompany?.name)}</Typography>
              </LabelWithChildren>

              {/* coach company */}
              <LabelWithChildren label={'Numéro de téléphone'}>
                <Typography variant={'tag'}>{valueRender(coachCompany?.phoneNumber)}</Typography>
              </LabelWithChildren>

              {/* coach company */}
              <LabelWithChildren label={"Numéro d'urgence"}>
                <Typography variant={'tag'}>
                  {valueRender(coachCompany?.emergencyNumber)}
                </Typography>
              </LabelWithChildren>

              {/* coach company */}
              <LabelWithChildren label={'Heure de RDV du prestataire étranger'}>
                <Typography variant={'tag'}>
                  {valueRender(
                    TripBookingProperties.getTripStep(tripBooking)?.foreignProviderAppointmentTime,
                    (value) => DateFormatter.getFormattedTime(value)
                  )}
                </Typography>
              </LabelWithChildren>

              {/* coach company */}
              <LabelWithChildren label={'Lieu de RDV du prestataire étranger'}>
                {foreignProviderAppointmentPlace ? (
                  <AddressLink
                    address1={foreignProviderAppointmentPlace.address1}
                    address2={foreignProviderAppointmentPlace.address2}
                    city={foreignProviderAppointmentPlace.city}
                    country={nativeCountryName(foreignProviderAppointmentPlace.country)}
                    zipCode={foreignProviderAppointmentPlace.zipCode}
                  />
                ) : (
                  <Typography variant={'tag'}>{valueRender(undefined)}</Typography>
                )}
              </LabelWithChildren>
            </div>
          )}

          {tripBooking.isTakeInCharge && (
            <div className={'grid grid-cols-3 gap-2'}>
              <LabelWithChildren label={'Lieu de RDV'}>
                <Typography variant={'tag'}>
                  {valueRender(tripBooking.meetingPlace?.label)}
                </Typography>
              </LabelWithChildren>

              <LabelWithChildren label={'Heure de RDV parents'}>
                <Badge
                  text={valueRender(tripBooking.meetingTimeForParents, (t) =>
                    DateFormatter.HHmm(DateFormatter.createDate(t))
                  )}
                />
              </LabelWithChildren>

              {tripBooking.meetingTimeForStaff && (
                <LabelWithChildren label={'Heure de RDV encadrants'}>
                  <Badge
                    text={DateFormatter.HHmm(
                      DateFormatter.createDate(tripBooking.meetingTimeForStaff)
                    )}
                  />
                </LabelWithChildren>
              )}
            </div>
          )}
        </div>
      )}

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'tag'} color={tailwindColors.primary.DEFAULT}>
          Encadrants français
        </Typography>

        {staffsQuery.data ? (
          <DataTable<TripBookingDispatchPlace>
            bodyVariant={'white'}
            data={staffsData ?? []}
            columns={staffColumns}
            tableClassName={'!rounded-lg'}
            rowCount={staffsData?.length ?? 0}
            paginated={false}
          />
        ) : (
          <LoadingScreen />
        )}
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'tag'} color={tailwindColors.primary.DEFAULT}>
          Jeunes
        </Typography>

        {enrollmentsQuery.data ? (
          <DataTable<TripBookingDispatchPlace>
            tableClassName={'!rounded-lg'}
            bodyVariant={'white'}
            columns={enrollmentColumns}
            data={studentsData ?? []}
            rowCount={studentsData?.length ?? 0}
            paginated={false}
          />
        ) : (
          <LoadingScreen />
        )}
      </div>
    </div>
  )
}
